import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Stack, Textarea, TextInput, LoadingOverlay, Container } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import BreadcrumbsComponent from 'components/BreadcrumbsComponent';
import ModalPopupConfirmationComponent from 'components/ModalPopupConfirmationComponent';
import SecondaryButton from 'components/SecondaryButton';
import PrimaryButton from 'components/PrimaryButton';
import { masterGroupService } from 'services/lia/masterGroupService';
import { API_ENDPOINTS } from 'services/api';
import httpClient from 'services/httpClient';
import PageTitle from 'components/PageTitle';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';

const items = [
  { title: 'Master Group', href: '/app/lia/questions/group-questions' },
  { title: 'Edit Group', href: '' }
];

const groupSchema = z.object({
  msGroupQuestionID: z.string().nonempty('ID is required'),
  title: z.string().nonempty('Name is required'),
  desc: z.string().nonempty('Description is required'),
  moduleID: z.string().nonempty('Module is required')
});

type FormData = z.infer<typeof groupSchema>;

const EditMasterGroupPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const groupId = searchParams.get('id');
  const [opened, { open, close }] = useDisclosure();
  const [formData, setFormData] = useState<FormData | null>(null);

  /* eslint-disable */
  // Fetch modules
  const { data: modulesData, isLoading: isLoadingModules } = useQuery({
    queryKey: ['modules'],
    queryFn: async () => {
      try {
        const response = await httpClient.get(API_ENDPOINTS.MODULE.LIST);
        return response.data;
      } catch {
        return { data: [] };
      }
    }
  });
  /* eslint-enable */

  const { data: groupData, isLoading: isLoadingGroup } = useQuery({
    queryKey: ['group', groupId],
    queryFn: async () => {
      if (!groupId) return null;
      const response = await masterGroupService.getDetail(groupId);
      return response;
    },
    enabled: !!groupId,
    retry: 1
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    resolver: zodResolver(groupSchema),
    defaultValues: {
      msGroupQuestionID: groupId || '1'
    }
  });

  const updateMutation = useMutation({
    mutationFn: (data: FormData) => {
      return masterGroupService.update(data.msGroupQuestionID, data);
    },
    onSuccess: () => {
      navigate('/app/lia/questions/group-questions');
      showSuccessNotification({
        title: 'Updated Successfully!',
        message: 'Your group has been updated successfully'
      });
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Failed to update group',
        message: error?.message || 'Something went wrong please try again'
      });
    }
  });

  useEffect(() => {
    if (groupData?.data) {
      const group = Array.isArray(groupData.data) ? groupData.data[0] : groupData.data;

      if (group) {
        reset({
          msGroupQuestionID: group.msGroupQuestionID,
          title: group.title,
          desc: group.desc,
          moduleID: group.moduleID
        });
      }
    }
  }, [groupData, reset]);

  const onSubmit = (data: FormData) => {
    setFormData(data);
    open();
  };

  const processData = async () => {
    if (formData) {
      updateMutation.mutate(formData);
    }
    close();
  };

  if (!groupId) {
    navigate('/app/lia/questions/group-questions');
    return null;
  }

  const isLoading = isLoadingGroup || isLoadingModules;

  return (
    <Container fluid>
      <PageTitle title='Edit Group' />
      <BreadcrumbsComponent items={items} />

      <ModalPopupConfirmationComponent
        buttonText='Edit'
        statusProcess='edit'
        opened={opened}
        open={open}
        close={close}
        onSubmit={processData}
      />

      <div style={{ marginTop: '20px', position: 'relative' }}>
        <LoadingOverlay visible={isLoading || updateMutation.isPending} />

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack>
            <Controller
              name='title'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Name'
                  placeholder='Input group name...'
                  required
                  error={errors.title?.message}
                  disabled={isLoading}
                  {...field}
                />
              )}
            />

            <Controller
              name='desc'
              control={control}
              render={({ field }) => (
                <Textarea
                  label='Group Description'
                  description='The description of the group'
                  placeholder='Input description...'
                  required
                  error={errors.desc?.message}
                  disabled={isLoading}
                  {...field}
                />
              )}
            />

            <Controller
              name='msGroupQuestionID'
              control={control}
              render={({ field }) => <input type='hidden' {...field} />}
            />
          </Stack>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '20px'
            }}
          >
            <SecondaryButton
              text='Cancel'
              mr={10}
              onClick={() => navigate('/app/lia/questions/group-questions')}
            />
            <PrimaryButton
              text='Update'
              type='submit'
              loading={isSubmitting || updateMutation.isPending}
            />
          </div>
        </form>
      </div>
    </Container>
  );
};

export default EditMasterGroupPage;
